import { Button, type ButtonProps, cn } from '@seen/ui'
import { forwardRef, type ReactNode, useMemo } from 'react'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import type { Field } from '#src/components/core/form/FormField.tsx'
import { ParticipantNameplate } from '#src/components/nameplates/ParticipantNameplate.tsx'
import { UserPlaceholderNameplate } from '#src/components/nameplates/UserPlaceholderNameplate.tsx'
import { ParticipantOption } from '#src/components/options/ParticipantOption.tsx'
import { UserPlaceholderOption } from '#src/components/options/UserPlaceholderOption.tsx'
import { useCurrentPractitionerPerms } from '#src/hooks/useCurrentPractitionerPerms.ts'
import { useParticipants } from '#src/hooks/useParticipants.ts'

export const ParticipantComboboxButton = forwardRef<
  HTMLButtonElement,
  Field<string | null> & Omit<ButtonProps, 'value' | 'onChange'>
>(({ value, onChange, className, disabled, ...fieldRest }, ref) => {
  const options = useParticipantComboboxOptions(value)
  const { isIDT, isMOE, isTech } = useCurrentPractitionerPerms()
  return (
    <Combobox options={options} onSelect={onChange} disabled={disabled}>
      <Button
        variant="outline"
        className={cn('flex items-center gap-2 shadow-sm', className)}
        ref={ref}
        disabled={disabled}
        {...fieldRest}
      >
        {value && <ParticipantNameplate participant={value} />}
        {!value && (
          <UserPlaceholderNameplate>
            {isMOE && !isIDT && !isTech && 'Lead'}
            {!(isMOE && !isIDT && !isTech) && 'Participant'}
          </UserPlaceholderNameplate>
        )}
      </Button>
    </Combobox>
  )
})

ParticipantComboboxButton.displayName = 'ParticipantComboboxButton'

function useParticipantComboboxOptions(
  selectedValue: string | null | undefined
): { value: string | null; label: ReactNode; keywords: string[] }[] {
  const participants = useParticipants()
  const { isIDT, isMOE, isTech } = useCurrentPractitionerPerms()
  const options = useMemo(
    () => [
      {
        value: null,
        label: (
          <UserPlaceholderOption isSelected={!selectedValue}>
            {isMOE && !isIDT && !isTech && 'No lead'}
            {!(isMOE && !isIDT && !isTech) && 'No participant'}
          </UserPlaceholderOption>
        ),
        keywords: [
          ...(isMOE && !isIDT && !isTech ? ['No lead'] : []),
          ...(!(isMOE && !isIDT && !isTech) ? ['No participant'] : []),
        ],
      },
      ...participants.map((p) => ({
        value: p.reference.reference,
        label: (
          <ParticipantOption
            participant={p.reference}
            isSelected={p.reference.reference === selectedValue}
          />
        ),
        keywords: p.searchKeywords,
      })),
    ],
    [participants, selectedValue, isIDT, isMOE, isTech]
  )
  return options
}
